// Nocturnal Racing — global brand script. Kept tiny: anything section-specific
// lives in its own nr-*.js file loaded only by the section that needs it.
import { getScrollTop, getIntersectionRoot } from '@theme/scroll-container';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// After dark: snippets/nr-head.liquid marked <html data-nr-shift> before first paint from the default home turf.
// Use the footer wordmark's place when it has one, then re-check with every clock tick so the site flips at the
// real sunset and sunrise while the page is open.
const shift = window.nrShift;
const home = document.querySelector('[data-nr-sunrise]')?.dataset;
const homeLat = parseFloat(home?.lat);
const homeLng = parseFloat(home?.lng);
if (shift && Number.isFinite(homeLat) && Number.isFinite(homeLng)) {
  Object.assign(shift, { lat: homeLat, lng: homeLng, tz: home.tz || shift.tz });
}

// Night clock: the visitor's own local time, and whether the night shift is on back home. Real data, not a countdown.
// Same format as the pre-paint fill in sections/nr-hero.liquid (12-hour in the US, matching "6:14 PM ET").
// Built on first use: the first Intl formatter is a long task on a slow phone, and most pages show no clock.
let clockFormat;

function tickClocks() {
  const now = new Date();
  shift?.check();
  for (const clock of document.querySelectorAll('[data-nr-clock]')) {
    const time = clock.querySelector('[data-nr-clock-time]');
    clockFormat ||= new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
    if (time) time.textContent = clockFormat.format(now);
    shift?.paint(clock);
  }
}

tickClocks();
setInterval(tickClocks, 15000);
// Background tabs throttle timers and back/forward restores a frozen page: catch up the moment it's seen again
addEventListener('pageshow', tickClocks);
document.addEventListener('visibilitychange', () => document.hidden || tickClocks());

// Ticker speed follows scroll velocity (Web Animations API, compositor-only).
// Horizon scrolls the document on mobile and .page-wrapper on desktop, so listen to both.
let lastY = 0; // read when a ticker binds, not at load (reading the scroll position forces a layout)
let lastT = performance.now();
let boost = 0;
let raf = 0;

function applyBoost() {
  boost *= 0.92;
  for (const track of document.querySelectorAll('[data-nr-ticker-track]')) {
    if (track.closest('.is-paused')) continue;
    const anim = track.getAnimations()[0];
    if (anim) anim.playbackRate = 1 + Math.min(boost, 6);
  }
  raf = boost > 0.02 ? requestAnimationFrame(applyBoost) : 0;
}

function onScroll() {
  if (reduceMotion.matches) return;
  const y = getScrollTop();
  const now = performance.now();
  const dy = Math.abs(y - lastY);
  const dt = Math.max(now - lastT, 16);
  lastY = y;
  lastT = now;
  boost = Math.max(boost, (dy / dt) * 2.2);
  if (!raf) raf = requestAnimationFrame(applyBoost);
}

// Only pages that show a ticker listen to scroll at all (all tickers are switched off at the moment)
let tickerBound = false;
function bindTickerBoost() {
  if (tickerBound || !document.querySelector('[data-nr-ticker-track]')) return;
  tickerBound = true;
  lastY = getScrollTop();
  window.addEventListener('scroll', onScroll, { passive: true });
  document.querySelector('.page-wrapper')?.addEventListener('scroll', onScroll, { passive: true });
}

bindTickerBoost();

// Pause buttons on tickers and looping media (WCAG 2.2.2). One delegated handler.
document.addEventListener('click', (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-nr-pause]') : null;
  if (!btn) return;
  const host = btn.closest('[data-nr-pausable]');
  if (!host) return;
  const paused = !host.classList.contains('is-paused');
  host.classList.toggle('is-paused', paused);
  btn.textContent = paused ? btn.dataset.playLabel : btn.dataset.pauseLabel;
  const video = host.querySelector('video');
  if (video) {
    video.dataset.userPaused = paused ? '1' : '';
    if (paused) video.pause();
    else video.play().catch(() => {});
  }
});

// Silent loops in brand sections: load and play only while on screen, never with reduced
// motion. Posters are deferred too, so slots far down the page cost nothing at load.
const videoObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const { target: video, isIntersecting } of entries) {
            if (isIntersecting && video.dataset.poster && !video.poster) video.poster = video.dataset.poster;
            if (!isIntersecting || reduceMotion.matches || video.dataset.userPaused) {
              video.pause();
              continue;
            }
            if (video.dataset.src && !video.src) video.src = video.dataset.src;
            video.play().catch(() => {});
          }
        },
        { root: getIntersectionRoot(), rootMargin: '200px 0px' }
      )
    : null;

function watchVideos(root = document) {
  root.querySelectorAll('video[data-nr-autoplay]').forEach((video) => videoObserver?.observe(video));
}

// Start after load so an above-the-fold loop never competes with the LCP image.
if (document.readyState === 'complete') watchVideos();
else window.addEventListener('load', () => watchVideos(), { once: true });

// The theme editor re-renders sections in place.
document.addEventListener('shopify:section:load', (event) => {
  tickClocks();
  watchVideos(event.target);
  bindTickerBoost();
});

// Signup forms: keep the "you're on the list" note only on the form that was submitted.
function scopeSignupSuccess() {
  const posted = document.querySelectorAll('form.nr-signup [data-nr-signup-done]');
  const target = decodeURIComponent(window.location.hash.slice(1));
  if (!posted.length || !target) return;
  for (const done of posted) {
    const form = done.closest('form');
    if (form.id === target) continue;
    done.remove();
    form.querySelector('.nr-signup__row')?.removeAttribute('hidden');
  }
}

scopeSignupSuccess();

// "Send to your crew": native share sheet on phones, copy link elsewhere. Delegated so it
// also works in re-rendered sections and drawers.
document.addEventListener('click', async (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-nr-share]') : null;
  if (!btn) return;
  const { url, text, title } = btn.dataset;
  const label = btn.querySelector('[data-nr-share-label]');
  try {
    if (navigator.share) {
      await navigator.share({ title, text, url });
      return;
    }
    await navigator.clipboard.writeText(`${text} ${url}`);
    if (label) {
      const idle = (label.dataset.idle ??= label.textContent);
      label.textContent = btn.dataset.copied;
      setTimeout(() => {
        label.textContent = idle;
      }, 2000);
    }
  } catch (error) {
    if (error?.name !== 'AbortError') console.warn('share failed', error);
  }
});

// Cart drawer: Horizon's focus trap keeps the first and last buttons it saw on open, and the drawer re-renders after
// every add, so Tab walked out of it (desktop), and a modal drawer let Tab reach the browser once (phones). Wrap Tab
// over the drawer's live buttons at every size.
document.addEventListener('keydown', (event) => {
  const drawer = event.key === 'Tab' && document.querySelector('#cart-drawer dialog[open]');
  // Another modal on top (the garage dialog) owns the keyboard
  const modal = drawer && document.querySelector('dialog:modal');
  if (!drawer || (modal && modal !== drawer)) return;
  const stops = [
    ...drawer.querySelectorAll('a[href], button:enabled, input:not([type="hidden"]):enabled, select:enabled, textarea:enabled, [tabindex]:not([tabindex^="-"])'),
  ].filter((el) => el.getClientRects().length);
  if (!stops.length) return;
  const first = stops[0];
  const last = stops[stops.length - 1];
  if (!drawer.contains(document.activeElement)) {
    event.preventDefault();
    first.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  }
});

// Desktop drawers are non-modal (a sidebar): the page behind is dimmed (nr-brand.css), and a click on the dim closes
// the drawer, as the backdrop does on phones. A click on the dim targets .page-wrapper itself (its ::after).
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element) || !event.target.matches('.page-wrapper.page-wrapper--drawer-open')) return;
  document.querySelector('theme-drawer[open]')?.close?.();
});
